import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import * as React from 'react';
import { Hangar } from '../../../types';

type Props = {
  open: boolean;
  onClose: () => void;
  availableHangars: Hangar[];
  selectedHangarIds: string[];
  onConfirm: (hangarIds: string[]) => void;
};

/**
 * Dialog for selecting which hangars to display in the Ops view
 */
export const HangarPicker: React.FC<Props> = ({
  open,
  onClose,
  availableHangars,
  selectedHangarIds,
  onConfirm,
}) => {
  // Local state for selection (until confirm)
  const [localSelected, setLocalSelected] = React.useState<string[]>(selectedHangarIds);

  // Reset local state when dialog opens
  React.useEffect(() => {
    if (open) {
      setLocalSelected(selectedHangarIds);
    }
  }, [open, selectedHangarIds]);

  const handleToggle = (hangarId: string) => {
    setLocalSelected((prev) =>
      prev.includes(hangarId)
        ? prev.filter((id) => id !== hangarId)
        : [...prev, hangarId]
    );
  };

  const handleSelectAll = () => {
    setLocalSelected(availableHangars.map((h) => h.id));
  };

  const handleClearAll = () => {
    setLocalSelected([]);
  };

  const handleConfirm = () => {
    onConfirm(localSelected);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Select Hangars</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose which hangars to display in the Ops view
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button size="small" variant="outlined" onClick={handleSelectAll}>
            Select All
          </Button>
          <Button size="small" variant="outlined" onClick={handleClearAll}>
            Clear All
          </Button>
        </Stack>

        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {availableHangars.length === 0 ? (
            <Typography color="text.secondary">No hangars available</Typography>
          ) : (
            availableHangars.map((hangar) => (
              <FormControlLabel
                key={hangar.id}
                control={
                  <Checkbox
                    checked={localSelected.includes(hangar.id)}
                    onChange={() => handleToggle(hangar.id)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">{hangar.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {hangar.width}' x {hangar.depth}' ({Math.round(hangar.width * hangar.depth)} sq ft)
                    </Typography>
                  </Box>
                }
                sx={{ display: 'flex', width: '100%', mb: 1 }}
              />
            ))
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={localSelected.length === 0}
        >
          Confirm ({localSelected.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};
